import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Check, Hash, Link2, Plus, SquareCheck, Trash2, Type } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useContactProperties,
  useCreateContactProperty,
  useDeleteContactProperty,
  type ContactPropertyDef,
  type ContactPropertyKind,
} from '@/api/contactProperties'
import { useContactSuggestions, useDecideSuggestion, useUpdateContact } from '@/api/contacts'
import { ValeurProposee } from './ValeurProposee'
import type { Contact } from '@/types/contacts'

const ICONES: Record<ContactPropertyKind, typeof Type> = {
  text: Type,
  number: Hash,
  date: Calendar,
  checkbox: SquareCheck,
  url: Link2,
}
const TYPES: ContactPropertyKind[] = ['text', 'number', 'date', 'checkbox', 'url']

interface Props {
  contact: Contact | null | undefined
  phoneNumber: string
  darkMode: boolean
  locale: string
  /** Le gestionnaire crée et supprime les propriétés ; tout le monde les remplit. */
  peutGerer: boolean
}

/**
 * Les propriétés que l'entreprise ajoute à toutes ses fiches (« Modèle du
 * véhicule », « Dernier entretien »…). Une propriété créée ici apparaît sur
 * chaque contact ; ses valeurs se remplissent fiche par fiche.
 */
export function ProprietesContact({ contact, phoneNumber, darkMode, locale, peutGerer }: Props) {
  const { t } = useTranslation()
  const { data: proprietes = [] } = useContactProperties()
  const updateContact = useUpdateContact(phoneNumber)
  const creer = useCreateContactProperty()
  const supprimer = useDeleteContactProperty()
  // Valeurs entendues dans les appels, affichées dans le champ vide, à approuver.
  const { data: suggestions = [] } = useContactSuggestions(phoneNumber)
  const decider = useDecideSuggestion(phoneNumber)
  const proposition = (p: ContactPropertyDef, texte: string) => {
    const s = suggestions.find((x) => x.field === `prop:${p.id}`)
    if (!s) return null
    const erreur = () => toast.error(t('sidebar.toasts.error', 'Erreur'))
    return (
      <ValeurProposee
        texte={texte || String(s.value)}
        citation={s.quote}
        darkMode={darkMode}
        enCours={decider.isPending}
        onApprouver={() => decider.mutate({ id: s.id, accept: true }, { onError: erreur })}
        onRefuser={() => decider.mutate({ id: s.id, accept: false }, { onError: erreur })}
      />
    )
  }
  const affichageDe = (p: ContactPropertyDef, v: unknown): string => {
    if (p.kind === 'checkbox') return v ? t('contacts.suggestions.yes', 'Oui') : t('contacts.suggestions.no', 'Non')
    if (p.kind === 'date' && typeof v === 'string') {
      const [a, m, j] = v.split('-').map(Number)
      return new Date(a, m - 1, j).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    }
    if (p.kind === 'url') return String(v).replace(/^https?:\/\//, '')
    return String(v)
  }

  const [edition, setEdition] = useState<number | null>(null)
  const [texte, setTexte] = useState('')
  const [nouveauType, setNouveauType] = useState<ContactPropertyKind | null>(null)
  const [nouveauNom, setNouveauNom] = useState('')
  const [aSupprimer, setASupprimer] = useState<ContactPropertyDef | null>(null)
  const champ = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (edition !== null || nouveauType) champ.current?.focus()
  }, [edition, nouveauType])

  const valeurs = contact?.custom_fields || {}
  const nomType = (k: ContactPropertyKind) =>
    t(`contacts.properties.kinds.${k}`, { text: 'Texte', number: 'Nombre', date: 'Date', checkbox: 'Case à cocher', url: 'Lien' }[k])

  const enregistrer = async (p: ContactPropertyDef, valeur: string | boolean) => {
    setEdition(null)
    const avant = valeurs[String(p.id)]
    if (typeof valeur === 'string' && valeur.trim() === String(avant ?? '')) return
    try {
      await updateContact.mutateAsync({ custom_fields: { [String(p.id)]: valeur } })
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error(
        code === 'invalid_custom_field'
          ? t('contacts.properties.invalid', '« {{label}} » : valeur non reconnue.', { label: p.label })
          : t('sidebar.toasts.error', 'Erreur'),
      )
    }
  }

  const affichage = (p: ContactPropertyDef): string => {
    const v = valeurs[String(p.id)]
    if (v === undefined || v === null || v === '') return ''
    if (p.kind === 'date' && typeof v === 'string') {
      const [a, m, j] = v.split('-').map(Number)
      return new Date(a, m - 1, j).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    }
    if (p.kind === 'number' && typeof v === 'number') return v.toLocaleString(locale === 'fr' ? 'fr-CA' : 'en-CA')
    if (p.kind === 'url') return String(v).replace(/^https?:\/\//, '')
    return String(v)
  }

  const creerPropriete = async () => {
    const label = nouveauNom.trim()
    const kind = nouveauType
    setNouveauType(null)
    setNouveauNom('')
    if (!label || !kind) return
    try {
      await creer.mutateAsync({ label, kind })
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error(
        code === 'duplicate'
          ? t('contacts.properties.duplicate', 'Cette propriété existe déjà.')
          : code === 'too_many'
            ? t('contacts.properties.tooMany', '50 propriétés au plus.')
            : t('sidebar.toasts.error', 'Erreur'),
      )
    }
  }

  const libelle = cn('text-[11px]', darkMode ? 'text-slate-500' : 'text-gray-400')
  const icone = cn('w-3 h-3 flex-shrink-0', darkMode ? 'text-slate-500' : 'text-gray-400')
  const valeurClasse = (vide: boolean) =>
    cn(
      'text-[11px] font-medium truncate max-w-[160px]',
      vide ? (darkMode ? 'text-slate-600 italic' : 'text-gray-300 italic') : darkMode ? 'text-slate-300' : 'text-gray-700',
    )

  return (
    <>
      {proprietes.map((p) => {
        const Icone = ICONES[p.kind]
        const cle = String(p.id)
        const corbeille = peutGerer && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setASupprimer(p)
            }}
            className={cn(
              'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity',
              darkMode ? 'text-slate-500 hover:text-red-400' : 'text-gray-300 hover:text-red-500',
            )}
            title={t('contacts.properties.delete', 'Supprimer la propriété')}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )

        if (p.kind === 'checkbox') {
          const coche = valeurs[cle] === true
          return (
            <div key={p.id} className="group flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Icone className={icone} />
                <span className={cn(libelle, 'truncate')}>{p.label}</span>
                {corbeille}
              </div>
              {!coche && suggestions.some((x) => x.field === `prop:${p.id}`) ? (
                proposition(p, affichageDe(p, suggestions.find((x) => x.field === `prop:${p.id}`)?.value))
              ) : (
              <button
                type="button"
                role="checkbox"
                aria-checked={coche}
                aria-label={p.label}
                onClick={() => void enregistrer(p, !coche)}
                className={cn(
                  'h-3.5 w-3.5 rounded border flex items-center justify-center transition-colors',
                  coche
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : darkMode
                      ? 'border-slate-600 hover:border-slate-400'
                      : 'border-gray-300 hover:border-gray-500',
                )}
              >
                {coche && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
              </button>
              )}
            </div>
          )
        }

        if (edition === p.id) {
          const touche = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void enregistrer(p, texte)
            }
            if (e.key === 'Escape') setEdition(null)
          }
          return (
            <div key={p.id} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Icone className={icone} />
                <span className={cn(libelle, 'truncate')}>{p.label}</span>
              </div>
              <input
                ref={champ}
                type={p.kind === 'date' ? 'date' : p.kind === 'number' ? 'text' : 'text'}
                inputMode={p.kind === 'number' ? 'decimal' : undefined}
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                onKeyDown={touche}
                onBlur={() => void enregistrer(p, texte)}
                className={cn(
                  'max-w-[160px] text-[11px] font-medium text-right bg-transparent border-0 border-b outline-none py-0 px-0',
                  darkMode ? 'text-white border-indigo-500/50' : 'text-gray-900 border-indigo-400/50',
                )}
              />
            </div>
          )
        }

        const texteAffiche = affichage(p)
        return (
          <div
            key={p.id}
            onClick={() => {
              setEdition(p.id)
              setTexte(valeurs[cle] === undefined ? '' : String(valeurs[cle]))
            }}
            className={cn(
              'group flex items-center justify-between py-1.5 cursor-pointer rounded-md transition-colors',
              darkMode ? 'hover:bg-slate-900/60' : 'hover:bg-gray-50/80',
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Icone className={icone} />
              <span className={cn(libelle, 'truncate')}>{p.label}</span>
              {corbeille}
            </div>
            {!texteAffiche && suggestions.some((x) => x.field === `prop:${p.id}`) ? (
              proposition(p, affichageDe(p, suggestions.find((x) => x.field === `prop:${p.id}`)?.value))
            ) : (
              <span className={valeurClasse(!texteAffiche)}>
                {texteAffiche || t('contacts.properties.empty', 'Ajouter')}
              </span>
            )}
          </div>
        )
      })}

      {peutGerer &&
        (nouveauType ? (
          <div className="flex items-center gap-2 py-1.5">
            {(() => {
              const Icone = ICONES[nouveauType]
              return <Icone className={icone} />
            })()}
            <input
              ref={champ}
              value={nouveauNom}
              maxLength={60}
              onChange={(e) => setNouveauNom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void creerPropriete()
                }
                if (e.key === 'Escape') {
                  setNouveauType(null)
                  setNouveauNom('')
                }
              }}
              onBlur={() => void creerPropriete()}
              placeholder={t('contacts.properties.namePlaceholder', 'Nom de la propriété ({{kind}})', {
                kind: nomType(nouveauType).toLowerCase(),
              })}
              className={cn(
                'flex-1 text-[11px] bg-transparent border-0 border-b outline-none py-0 px-0',
                darkMode
                  ? 'text-white border-indigo-500/50 placeholder:text-slate-600'
                  : 'text-gray-900 border-indigo-400/50 placeholder:text-gray-300',
              )}
            />
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center gap-2 py-1.5 text-[11px] transition-colors',
                  darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-700',
                )}
              >
                <Plus className="w-3 h-3" />
                {t('contacts.properties.add', 'Ajouter une propriété')}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {TYPES.map((k) => {
                const Icone = ICONES[k]
                return (
                  <DropdownMenuItem key={k} onSelect={() => setNouveauType(k)}>
                    <Icone className="w-4 h-4" />
                    {nomType(k)}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}

      <AlertDialog open={!!aSupprimer} onOpenChange={(o) => !o && setASupprimer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('contacts.properties.deleteTitle', 'Supprimer « {{label}} » ?', { label: aSupprimer?.label })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'contacts.properties.deleteBody',
                'La propriété disparaît de toutes les fiches, avec les valeurs déjà saisies.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Annuler')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (aSupprimer) {
                  supprimer.mutate(aSupprimer.id, {
                    onError: () => toast.error(t('sidebar.toasts.error', 'Erreur')),
                  })
                }
                setASupprimer(null)
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('contacts.properties.deleteConfirm', 'Supprimer')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
